import { Type, type TLiteral } from '@sinclair/typebox';
import { validateEnumTranslations } from '../../safety_checks';

//⚠️IMPACT attention changer aussi l'enum Prisma `BookingSource` dans schema.prisma (MÊME ordre des valeurs)
/**
 * Canal d'entrée d'une réservation (statistiques de provenance). Posé à la création, jamais
 * réécrit. NULL en base = antérieur au backfill ou canal inconnu (« Non renseigné »).
 */
export enum BookingSource {
  WEBSITE = 'WEBSITE',
  WIDGET = 'WIDGET',
  GOOGLE = 'GOOGLE',
  GOOGLE_RESERVE = 'GOOGLE_RESERVE',
  DIRECT_LINK = 'DIRECT_LINK',
  CAMPAIGN = 'CAMPAIGN',
  ONLINE = 'ONLINE',
  PRO_AGENDA = 'PRO_AGENDA',
  ASSISTANT = 'ASSISTANT',
  IMPORT_V1 = 'IMPORT_V1',
}

export const BOOKING_SOURCE_TRANSLATE_KEY = 'BOOKING_SOURCE_';

export const BookingSourceTranslation = {
  'fr-FR': {
    [`${BOOKING_SOURCE_TRANSLATE_KEY}${BookingSource.WEBSITE}`]: 'Site internet',
    [`${BOOKING_SOURCE_TRANSLATE_KEY}${BookingSource.WIDGET}`]: 'Widget intégré',
    [`${BOOKING_SOURCE_TRANSLATE_KEY}${BookingSource.GOOGLE}`]: 'Fiche Google',
    [`${BOOKING_SOURCE_TRANSLATE_KEY}${BookingSource.GOOGLE_RESERVE}`]: 'Réserver avec Google',
    [`${BOOKING_SOURCE_TRANSLATE_KEY}${BookingSource.DIRECT_LINK}`]: 'Lien direct',
    [`${BOOKING_SOURCE_TRANSLATE_KEY}${BookingSource.CAMPAIGN}`]: 'Campagne email / SMS',
    [`${BOOKING_SOURCE_TRANSLATE_KEY}${BookingSource.ONLINE}`]: 'Réservation en ligne',
    [`${BOOKING_SOURCE_TRANSLATE_KEY}${BookingSource.PRO_AGENDA}`]: 'Agenda (ajout manuel)',
    [`${BOOKING_SOURCE_TRANSLATE_KEY}${BookingSource.ASSISTANT}`]: 'Assistant IA (Pulse ou agent via MCP)',
    [`${BOOKING_SOURCE_TRANSLATE_KEY}${BookingSource.IMPORT_V1}`]: 'Import de l’ancienne version',
  },
};

validateEnumTranslations(BookingSource, BookingSourceTranslation, BOOKING_SOURCE_TRANSLATE_KEY, true);

/**
 * Canaux qu'un client HTTP public (tunnel /book, site du pro, widget embarqué) a le droit de
 * déclarer. Les canaux internes (agenda, assistant, import) sont posés par le serveur et refusés
 * depuis un body public.
 */
export const PUBLIC_BOOKING_SOURCES = [
  BookingSource.WEBSITE,
  BookingSource.WIDGET,
  BookingSource.GOOGLE,
  BookingSource.GOOGLE_RESERVE,
  BookingSource.DIRECT_LINK,
  BookingSource.CAMPAIGN,
] as const;
export type PublicBookingSource = (typeof PUBLIC_BOOKING_SOURCES)[number];

export const BookingSourceSchema = Type.Enum(BookingSource);
export const PublicBookingSourceSchema = Type.Union(
  PUBLIC_BOOKING_SOURCES.map((value): TLiteral<PublicBookingSource> => Type.Literal(value)),
);

export function isPublicBookingSource(value: unknown): value is PublicBookingSource {
  return typeof value === 'string' && (PUBLIC_BOOKING_SOURCES as readonly string[]).includes(value);
}

/** Paramètre d'URL porté par les liens de réservation (`?src=google`). */
export const BOOKING_SOURCE_URL_PARAM = 'src';
/** Paramètre d'URL de l'attribution de campagne (`?cid=<pro_campaign_id>`). */
export const BOOKING_CAMPAIGN_URL_PARAM = 'cid';

const URL_PARAM_BY_SOURCE: Record<PublicBookingSource, string> = {
  [BookingSource.WEBSITE]: 'website',
  [BookingSource.WIDGET]: 'widget',
  [BookingSource.GOOGLE]: 'google',
  [BookingSource.GOOGLE_RESERVE]: 'google_reserve',
  [BookingSource.DIRECT_LINK]: 'direct',
  [BookingSource.CAMPAIGN]: 'campaign',
};

export function bookingSourceToUrlParam(source: PublicBookingSource): string {
  return URL_PARAM_BY_SOURCE[source];
}

/** Valeur de `?src=` → canal public, `null` si absent ou inconnu (jamais d'erreur côté client). */
export function parseBookingSourceParam(value: string | string[] | null | undefined): PublicBookingSource | null {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== 'string' || raw.length === 0) return null;
  const normalized = raw.trim().toLowerCase();
  for (const source of PUBLIC_BOOKING_SOURCES) {
    if (URL_PARAM_BY_SOURCE[source] === normalized) return source;
  }
  return null;
}
